import { useLoading } from "../../contexts/LoadingContext";
import { useSetting } from "../../contexts/SettingContext";
import { useFetch } from "../../utils/FetchTemplate";
import { FormField } from "../../interfaces/User.interface";
import PortraitOutlinedIcon from '@mui/icons-material/PortraitOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import MedicationIcon from '@mui/icons-material/Medication';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { FormBuilder } from "../../utils/FormBuilder/FormBuilder";
import { generateValidationSchema } from "../../utils/FormBuilder/Validators";
import { set, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormSection, SimpleFormSection } from "../../components/Sections";
import { Avatar, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useDialog } from "../../contexts/DialogContext";
import { useAuth } from "../../contexts/AuthContext";
import Cropper, { ReactCropperElement } from 'react-cropper';
import { PhotoCamera } from "@mui/icons-material";
import { useMembers } from "../../contexts/MemberContext";

interface ProfilePictureDisplayProps {
  image: string | ArrayBuffer | null;
  onImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const ProfilePictureDisplay: React.FC<ProfilePictureDisplayProps> = ({ image, onImageChange }) => {
  return (
    <div className="flex flex-col items-center">
      <input
        accept="image/*"
        className="hidden"
        id="icon-button-file"
        type="file"
        onChange={onImageChange}
      />
      <label htmlFor="icon-button-file">
        <Avatar
          src={image as string}
          alt="Profile Picture"
          className="mb-4 cursor-pointer transition-transform transform hover:scale-110"
          sx={{ width: 100, height: 100 }}
          component="span"
        />
      </label>
    </div>
  );
};

interface ProfilePictureUploadProps {
  profilePicture: string;
  setProfilePicture: (event: string ) => void;
}
const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ( {profilePicture, setProfilePicture} ) => {
  const { settings, fetchSettings } = useSetting();
  const {postFile} = useFetch();
  const [updatedImage, setUpdatedImage] = useState<boolean>(false);
  const handleImageUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    const response = await postFile('/api/setting/profile/update/picture', formData).then(
      (response) => {
        fetchSettings();
      }
    )
  };
  
  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleImageUpload(file);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <ProfilePictureDisplay image={profilePicture} onImageChange={handleImageChange} />
    </div>
  );
};


const firstName: FormField = {
  name: "firstName",
  question: "First Name",
  validation: "text",
  required: true,
  questionType: "text",
  icon: <PortraitOutlinedIcon />
};

const lastName: FormField = {
  name: "lastName",
  question: "Last Name",
  validation: "text",
  required: true,
  questionType: "text",
  icon: <PortraitOutlinedIcon />
};

const email: FormField = {
  name: "email",
  question: "Email",
  validation: "email",
  required: true,
  questionType: "text",
  icon: <MailOutlinedIcon />
};

const clinicName: FormField = {
  name: "clinicName",
  question: "Clinic Name", // Changed to preferred name as required, did not change the backend
  validation: "text",
  required: false,
  questionType: "text",
  icon: <MedicationIcon />
};

export const ProfilePage: React.FC<{}> = () => {
  const showDialog = useDialog();
  const { settings, fetchSettings } = useSetting();
  const [profilePicture, setProfilePicture] = useState<string>(process.env.REACT_APP_BACKEND_URL + '/uploads/default.jpg'); 
  const { postData, getData } = useFetch()
  const [formFields, setFormFields] = useState<FormField[]>([firstName, lastName, email, clinicName]);
  const { loggedIn, user } = useAuth()
  useEffect(() => {
    setFormFields([firstName, lastName, email, clinicName]);
    
  }, []);

  useEffect(() => {
    setProfilePicture(process.env.REACT_APP_BACKEND_URL + settings.profilePicture);
  }, [settings]);
  const validationSchema = useMemo(
    () => generateValidationSchema(formFields),
    [formFields]
  );

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange', // Validate on change
    defaultValues: settings.profile
  });

  useEffect(() => {
    reset(settings.profile);
  }, [settings, reset]);

  const onSubmit = (data: any) => {
    postData('/api/setting/profile/update', { 
      updateData: {... data, profilePicture}, 
      userId: user.userId }).then(() => {
      fetchSettings();
      showDialog('success', 'Profile updated successfully');
    });
  }

  const formContent = useMemo(() => FormBuilder(formFields, control, errors), [formFields, control, errors]);

  return (
    <div>

      <form onSubmit={handleSubmit(onSubmit)} id="profileForm" className="space-y-4">
        <div className="flex justify-center items-center h-1/2">
          <ProfilePictureUpload profilePicture={profilePicture} setProfilePicture={setProfilePicture}/>
        </div>
        <FormSection title="Profile">
          {formContent}
        </FormSection>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
        >
          update Profile
        </Button>
      </form>
    </div>
  )
}